import {
  type GridPosition,
  type InteractionSource,
  type SelectionRange,
  type SelectionState,
  type SelectionMode,
} from '../../../state/types';
import { type ReducerResponse, type SelectionAxis } from '../../types';
import { mergeRanges } from '../range/merge-ranges';

type MergeAdjacentRangesParams = {
  state: SelectionState;
  range1: SelectionRange;
  range2: SelectionRange;
  axis: SelectionAxis;
  newIndex: number;
  source: InteractionSource;
  mode: SelectionMode;
};

/**
 * Merges two adjacent ranges into one contiguous range.
 * @returns A reducer response with updated state and delta
 */
export function mergeAdjacentRanges({
  state,
  range1,
  range2,
  axis,
  newIndex,
  source,
  mode,
}: MergeAdjacentRangesParams): ReducerResponse {
  const mergedRange = mergeRanges({
    range1,
    range2,
    newIndex,
    axis,
  });

  const updatedSelections = state.selections
    .filter((r) => r.id !== range1.id && r.id !== range2.id)
    .concat([mergedRange]);

  const focusedCell: GridPosition =
    axis === 'row'
      ? { row: { index: newIndex }, col: { index: 0 } }
      : { row: { index: 0 }, col: { index: newIndex } };

  const delta: ReducerResponse['delta'] = {
    kind: 'selectionModified',
    source,
    rangeIdsToRemove: [range1.id, range2.id],
    added: [mergedRange],
    focusChange: {
      prev: state.focusedCell,
      next: focusedCell,
    },
  };

  return {
    delta,
    next: {
      ...state,
      mode,
      selections: updatedSelections,
      focusedCell,
      lastInteractionSource: source,
    },
  };
}
